import React, { useState } from 'react';
import { X, Download, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/api';
import { TID } from '@/testIds';

export default function RateSheetGateModal({ open, onClose }) {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/rates/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, company: company || undefined }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'motor-inks-wholesale-rate-card.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success('Rate sheet downloaded — check your downloads folder.');
      setEmail(''); setCompany('');
      onClose();
    } catch (err) {
      toast.error('Download failed. Please try again or email sales@motorinks.com.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
         onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg mi-surface border-t-4 border-t-[#E63946] p-8"
      >
        <button
          onClick={onClose}
          data-testid={TID.rateGateClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white p-1"
          aria-label="close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 border mi-line-strong grid place-items-center flex-shrink-0">
            <Download className="w-5 h-5 text-[#E63946]" />
          </div>
          <div>
            <div className="mi-eyebrow text-[#E63946]">// CONFIDENTIAL RATE SHEET</div>
            <div className="mi-headline text-2xl mt-1 leading-tight">
              Get the branded PDF.<br />
              <span className="text-[#E63946]">Instant download.</span>
            </div>
          </div>
        </div>

        <p className="text-white/70 text-sm leading-relaxed mb-5">
          Drop your email and we'll send you the full wholesale rate card as a branded PDF — SQF, FLT,
          PNL, AFT, PRO and ACC codes with production standards. No spam, no follow-up unless you ask for it.
        </p>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <div className="mi-eyebrow mb-1.5 text-white/70">Work email · required</div>
            <div className="relative">
              <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                required
                type="email"
                placeholder="you@shop.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={TID.rateGateEmail}
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm pl-10 pr-4 py-3 text-white text-sm placeholder-white/40 hover:border-white/25"
              />
            </div>
          </label>

          <label className="block">
            <div className="mi-eyebrow mb-1.5 text-white/70">Company · optional</div>
            <input
              type="text"
              placeholder="Your shop or agency name"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              data-testid={TID.rateGateCompany}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-4 py-3 text-white text-sm placeholder-white/40 hover:border-white/25"
            />
          </label>

          <button
            type="submit"
            disabled={busy || !email}
            data-testid={TID.rateGateSubmit}
            className="mi-btn-red w-full disabled:opacity-60"
          >
            {busy ? 'Preparing PDF…' : (<><Download className="w-4 h-4" /> Download rate sheet</>)}
          </button>

          <div className="flex items-center justify-center gap-2 text-[10px] mi-eyebrow text-white/40 pt-2">
            <ShieldCheck className="w-3 h-3" /> WE NEVER SELL OR SHARE YOUR EMAIL
          </div>
        </form>
      </div>
    </div>
  );
}
